import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import FormsAPI from '../../api/forms';

export const state = {
  records: [],
  submissions: {},
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isFetchingSubmissions: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getAll(_state) {
    return _state.records.sort((a, b) => b.id - a.id);
  },
  getById: _state => id => {
    return _state.records.find(record => record.id === Number(id)) || {};
  },
  getPublished(_state) {
    return _state.records.filter(record => record.status === 'published');
  },
  getDrafts(_state) {
    return _state.records.filter(
      record => record.status === 'draft' || !record.status
    );
  },
  getArchived(_state) {
    return _state.records.filter(record => record.status === 'archived');
  },
  getSubmissions: _state => formId => {
    return _state.submissions[formId] || { data: [], meta: {} };
  },
};

export const actions = {
  get: async function getForms({ commit }) {
    commit(types.SET_FORM_UI_FLAG, { isFetching: true });
    try {
      const response = await FormsAPI.get();
      const data = response.data.payload || response.data;
      commit(types.SET_FORMS, Array.isArray(data) ? data : []);
    } catch (error) {
      // Ignore error
    } finally {
      commit(types.SET_FORM_UI_FLAG, { isFetching: false });
    }
  },

  show: async function showForm({ commit }, formId) {
    commit(types.SET_FORM_UI_FLAG, { isFetching: true });
    try {
      const response = await FormsAPI.show(formId);
      const form = response.data.payload || response.data;
      commit(types.SET_FORM, form);
      return form;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_FORM_UI_FLAG, { isFetching: false });
    }
  },

  create: async function createForm({ commit }, formObj) {
    commit(types.SET_FORM_UI_FLAG, { isCreating: true });
    try {
      const response = await FormsAPI.create(formObj);
      const form = response.data.payload || response.data;
      commit(types.ADD_FORM, form);
      return form;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_FORM_UI_FLAG, { isCreating: false });
    }
  },

  update: async ({ commit }, { id, ...updateObj }) => {
    commit(types.SET_FORM_UI_FLAG, { isUpdating: true });
    try {
      const response = await FormsAPI.update(id, updateObj);
      const form = response.data.payload || response.data;
      commit(types.EDIT_FORM, form);
      return form;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_FORM_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async ({ commit }, id) => {
    commit(types.SET_FORM_UI_FLAG, { isDeleting: true });
    try {
      await FormsAPI.delete(id);
      commit(types.DELETE_FORM, id);
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_FORM_UI_FLAG, { isDeleting: false });
    }
  },

  duplicate: async ({ commit }, formId) => {
    commit(types.SET_FORM_UI_FLAG, { isCreating: true });
    try {
      const response = await FormsAPI.duplicate(formId);
      const form = response.data.payload || response.data;
      commit(types.ADD_FORM, form);
      return form;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_FORM_UI_FLAG, { isCreating: false });
    }
  },

  publish: async ({ commit }, formId) => {
    commit(types.SET_FORM_UI_FLAG, { isUpdating: true });
    try {
      const response = await FormsAPI.publish(formId);
      const form = response.data.payload || response.data;
      commit(types.EDIT_FORM, form);
      return form;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_FORM_UI_FLAG, { isUpdating: false });
    }
  },

  archive: async ({ commit }, formId) => {
    commit(types.SET_FORM_UI_FLAG, { isUpdating: true });
    try {
      const response = await FormsAPI.archive(formId);
      const form = response.data.payload || response.data;
      commit(types.EDIT_FORM, form);
      return form;
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_FORM_UI_FLAG, { isUpdating: false });
    }
  },

  fetchSubmissions: async ({ commit }, { formId, params }) => {
    commit(types.SET_FORM_UI_FLAG, { isFetchingSubmissions: true });
    try {
      const response = await FormsAPI.getSubmissions(formId, params);
      const payload = response.data;
      commit(types.SET_FORM_SUBMISSIONS, {
        formId,
        data: payload.data || payload.payload || [],
        meta: payload.meta || {},
      });
    } catch (error) {
      throw new Error(error);
    } finally {
      commit(types.SET_FORM_UI_FLAG, { isFetchingSubmissions: false });
    }
  },
};

export const mutations = {
  [types.SET_FORM_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_FORMS]: MutationHelpers.set,
  [types.ADD_FORM]: MutationHelpers.create,
  [types.SET_FORM]: MutationHelpers.setSingleRecord,
  [types.EDIT_FORM]: MutationHelpers.update,
  [types.DELETE_FORM]: MutationHelpers.destroy,

  [types.SET_FORM_SUBMISSIONS](_state, { formId, data, meta }) {
    _state.submissions = {
      ..._state.submissions,
      [formId]: { data, meta },
    };
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
